import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Space } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import CustomersDropDown from "../../Loyalty/CustomersDropdown";
import StoresDropDown from "../StoresDropDown";
import InfoTile from "./InfoTile";
import ItemsRankingGrid from "./ItemsRankingGrid";
import ItemSalesTable from "./ItemSalesTable";
import ItemSalesChart from "./ItemSalesChart";
import {
  MainContainer,
  BackButton,
  FullWidthSpace,
  FullWidthVerticalSpace,
  PageTitle,
  InheritText,
  SummarySection,
  SummaryItem,
} from "./styles";
import {
  DateRange,
  Merchant,
  User,
  ChartType,
  MetricOption,
  SortKey,
} from "./constants";
import { useTheme } from "../../../context/ThemeContext";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchItemSales, fetchStores as fetchItemStores } from "../../../redux/ItemSales";
import { StyledText } from "./styles";

const ItemSales: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  
  const { items, keyMetrics, loading } = useSelector(
    (state: RootState) => state.itemSales
  );
  
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>({
    startDate: moment("2023-01-01"),
    endDate: moment(),
  });
  const [chartType, setChartType] = useState<ChartType>("line");
  const [selectedFilter] = useState<string>("");
  const [metricOption, setMetricOption] = useState<MetricOption>("totalRevenue");
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    isAscending: boolean;
  }>({
    key: "item",
    isAscending: true,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(8);
  const [storeId, setStoreid] = useState<string>("");

  const userString = localStorage.getItem("user");
  const user: User | null = userString ? JSON.parse(userString) : null;

  useEffect(() => {
    if (user?.role === "merchant" && !merchant) {
      setMerchant(user as Merchant);
    }
  }, [user, merchant]);

  useEffect(() => {
    if (user?.role !== "admin" && !storeId) {
      dispatch(fetchItemStores()).then((result) => {
        if (fetchItemStores.fulfilled.match(result) && result.payload.length > 0) {
          setStoreid(result.payload[0].id);
        }
      });
    }
  }, [user, storeId, dispatch]);

  const handleSelection = (selection: DateRange | { startDate: Date | null; endDate: Date | null }) => {
    let startDate: moment.Moment;
    let endDate: moment.Moment;
    
    if (moment.isMoment(selection.startDate)) {
      startDate = selection.startDate;
    } else if (selection.startDate) {
      startDate = moment(selection.startDate);
    } else {
      startDate = moment("2023-01-01");
    }
    
    if (moment.isMoment(selection.endDate)) {
      endDate = selection.endDate;
    } else if (selection.endDate) {
      endDate = moment(selection.endDate);
    } else {
      endDate = moment();
    }
    
    setSelectedDateRange({ startDate, endDate });
  };

  useEffect(() => {
    if (storeId || user?.role === "admin") {
      const params = isFilter
        ? {
            filter: selectedFilter,
            store: storeId,
            merchantId: merchant?.id,
          }
        : {
            startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
            endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
            store: storeId,
            merchantId: merchant?.id,
          };
      
      dispatch(fetchItemSales(params));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter, selectedDateRange, isFilter, merchant, storeId, dispatch, user?.role]);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      isAscending: prev.key === key ? !prev.isAscending : true,
    }));
  };

  const sortedData = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const { key, isAscending } = sortConfig;
      const valueA = a[key];
      const valueB = b[key];

      if (typeof valueA === "string") {
        return isAscending
          ? valueA.localeCompare(String(valueB))
          : String(valueB).localeCompare(valueA);
      }
      return isAscending 
        ? (parseFloat(String(valueA)) || 0) - (parseFloat(String(valueB)) || 0)
        : (parseFloat(String(valueB)) || 0) - (parseFloat(String(valueA)) || 0);
    });
    return sorted;
  }, [items, sortConfig]);

  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return sortedData.slice(startIdx, endIdx);
  }, [sortedData, currentPage, itemsPerPage]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const themeMode: "light" | "dark" = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <StyledThemeProvider theme={styledTheme}>
      <MainContainer>
        <FullWidthSpace direction="vertical" size="large">
          <Row align="middle" gutter={16}>
            <Col>
              <BackButton
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                onClick={() => navigate(-1)}
                shape="circle"
                size="large"
                aria-label="Go Back"
              />
            </Col>
            <Col flex={1}>
              <FullWidthVerticalSpace direction="vertical" size={0}>
                <Row justify="space-between" align="middle">
                  <Col>
                    <PageTitle level={2}>Item Sales</PageTitle>
                  </Col>
                  <Col>
                    <Space wrap>
                      {user?.role === "admin" && (
                        <Space>
                          <InheritText>By Merchants</InheritText>
                          <CustomersDropDown
                            onOptionSelect={(merch: Merchant) => {
                              setMerchant(merch);
                            }}
                          />
                        </Space>
                      )}
                      <Space>
                        <InheritText>By Store</InheritText>
                        <StoresDropDown
                          onOptionSelect={(store: { id: string }) => {
                            setStoreid(store.id);
                          }}
                          merchantId={merchant?.id}
                          role={user?.role}
                        />
                      </Space>
                    </Space>
                  </Col>
                </Row>
                <StyledText type="secondary">
                  Analyze sales performance by items
                </StyledText>
              </FullWidthVerticalSpace>
            </Col>
          </Row>

          <SummarySection>
            <SummaryItem>
              <InfoTile
                title="Total Revenue"
                subTitle="Overall revenue from all items"
                icon={faDollarSign}
                merchant={merchant}
                storeId={storeId}
              />
            </SummaryItem>
            <SummaryItem>
              <ItemsRankingGrid data={items} isLoading={loading} />
            </SummaryItem>
          </SummarySection>

          <ItemSalesChart
            data={items}
            chartType={chartType}
            metricOption={metricOption}
            selectedDateRange={selectedDateRange}
            onDateRangeChange={(range: DateRange) => {
              setIsFilter(false);
              setSelectedDateRange(range);
            }}
            onDateSelect={handleSelection}
            onMetricChange={(option: MetricOption) => setMetricOption(option)}
            onChartTypeChange={(type: ChartType) => setChartType(type)}
            keyMetrics={keyMetrics}
            isLoading={loading}
          />

          <ItemSalesTable
            data={paginatedData}
            sortConfig={sortConfig}
            onSort={handleSort}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onItemsPerPageChange={(lim: number) => setItemsPerPage(lim)}
            isLoading={loading}
          />
        </FullWidthSpace>
      </MainContainer>
    </StyledThemeProvider>
  );
};

export default ItemSales;
